import {
  RecurringTemplateSchema,
  ZodCreateRecurringTemplateSchema,
  ZodEditRecurringTemplateSchema,
  ZodGetRecurringTemplatesParams,
} from "@billingnow/zod-schema";
import { PaginationMeta, PaginationParams } from "./pagination";
import { apiClient } from "./client";

export type RecurringTemplate = RecurringTemplateSchema;

/**
 * Get a single recurring template by ID
 */
export const getRecurringTemplate = async (templateId: string): Promise<RecurringTemplate> => {
  const response = await apiClient.get<{ recurringTemplate: RecurringTemplate }>(`/api/recurring-template/${templateId}`);
  return response.data.recurringTemplate;
};

/**
 * Get all recurring templates - returns all templates (for backward compatibility)
 * @deprecated Use getRecurringTemplatesPaginated for better performance with large datasets
 */
export const getRecurringTemplates = async (params?: ZodGetRecurringTemplatesParams): Promise<RecurringTemplate[]> => {
  const response = await apiClient.get<{ recurringTemplates: RecurringTemplate[]; pagination: PaginationMeta }>(
    "/api/recurring-template",
    { params },
  );
  return response.data.recurringTemplates;
};

/**
 * Paginated response for recurring templates
 */
export interface GetRecurringTemplatesPaginatedResponse {
  recurringTemplates: RecurringTemplate[];
  pagination: PaginationMeta;
}

/**
 * Get recurring templates with pagination support
 */
export const getRecurringTemplatesPaginated = async (
  params?: ZodGetRecurringTemplatesParams & PaginationParams,
): Promise<GetRecurringTemplatesPaginatedResponse> => {
  const response = await apiClient.get<GetRecurringTemplatesPaginatedResponse>("/api/recurring-template", { params });
  return response.data;
};

export interface CreateRecurringTemplateResponse {
  recurringTemplate: RecurringTemplate;
  recurringTemplateId: string;
  generatedInvoiceIds: string[];
  message: string;
}

export interface UpdateRecurringTemplateResponse {
  recurringTemplate: RecurringTemplate;
  generatedInvoiceIds: string[];
  message: string;
}

/**
 * Create a new recurring template.
 * If `nextIssueDate` is in the past, the server runs catch-up generation —
 * `generatedInvoiceIds` lists the resulting invoices and the returned template
 * reflects any status change (e.g. auto-deactivated when the end condition is hit).
 */
export const createRecurringTemplate = async (
  templateData: ZodCreateRecurringTemplateSchema,
): Promise<CreateRecurringTemplateResponse> => {
  const response = await apiClient.post<CreateRecurringTemplateResponse>("/api/recurring-template", templateData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

/**
 * Update an existing recurring template.
 * If the edit leaves `nextIssueDate` in the past, the server runs catch-up generation —
 * `generatedInvoiceIds` lists the resulting invoices and the returned template
 * reflects any status change.
 */
export const updateRecurringTemplate = async (
  templateData: ZodEditRecurringTemplateSchema,
): Promise<UpdateRecurringTemplateResponse> => {
  const response = await apiClient.put<UpdateRecurringTemplateResponse>(
    `/api/recurring-template/${templateData.id}`,
    templateData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

/**
 * Toggle recurring template active status (pause/resume)
 */
export const toggleRecurringTemplate = async (
  templateId: string,
  isActive: boolean,
): Promise<{ recurringTemplate: RecurringTemplate; message: string }> => {
  const response = await apiClient.patch<{ recurringTemplate: RecurringTemplate; message: string }>(
    `/api/recurring-template/${templateId}/toggle`,
    { isActive },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

/**
 * Delete a recurring template
 */
export const deleteRecurringTemplate = async (templateId: string): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ message: string }>(`/api/recurring-template/${templateId}`);
  return response.data;
};

/**
 * Manually generate an invoice from a recurring template
 */
export const generateInvoiceFromTemplate = async (
  templateId: string,
): Promise<{ invoiceId: string; message: string }> => {
  const response = await apiClient.post<{ invoiceId: string; message: string }>(
    `/api/recurring-template/${templateId}/generate`,
    {},
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

/**
 * Fetch all recurring templates by iterating through all pages
 * Used for export functionality to get complete dataset
 */
export const getAllRecurringTemplates = async (
  params?: ZodGetRecurringTemplatesParams,
): Promise<RecurringTemplate[]> => {
  const allTemplates: RecurringTemplate[] = [];
  let cursor: string | null = null;

  do {
    const response = await getRecurringTemplatesPaginated({ ...params, cursor, limit: 100 });
    allTemplates.push(...response.recurringTemplates);
    cursor = response.pagination.nextCursor;
  } while (cursor);

  return allTemplates;
};

export default getRecurringTemplate;
